import { useState, useEffect, useRef } from 'react';
import { Phone, Menu, X, ChevronDown, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackPhoneCall } from '@/utils/analytics';

const LOGO_URL = '/logo.jpg';

const serviceDropdownItems = [
  { label: 'Electrical Panel Upgrades', to: '/services/electrical-panel-upgrade-nashville' },
  { label: 'EV Charger Installation', to: '/services/ev-charger-installation-nashville' },
  { label: 'Whole-Home Backup Power', href: 'https://generatorsofnashville.com', testId: 'backup-power' },
  { label: 'Electrical Repairs', to: '/services/electrical-repair-nashville' },
  { label: 'Lighting & Ceiling Fans', to: '/services/lighting-installation-nashville' },
  { label: 'Surge Protection', to: '/services/surge-protection-nashville' },
  { label: 'Electrical Inspections', to: '/services/electrical-inspection-nashville' },
  { label: 'Pool & Spa Wiring', to: '/services/pool-spa-wiring-nashville' },
];

const serviceCategories = [
  { label: 'All Residential Services', to: '/services/residential-electrical-services-nashville' },
  { label: 'All Commercial Services', to: '/services/commercial-electrical-services-nashville' },
];

const navLinks = [
  { label: 'About', href: '/#about' },
  { label: 'Process', href: '/#process' },
  { label: 'Gallery', href: '/#gallery' },
  { label: 'Blog', href: '/blog' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/#contact' },
];

function ServicesDropdown({ scrolled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="nav-services-dropdown"
    >
      <button
        className={`flex items-center gap-1 font-body text-xs uppercase tracking-widest transition-colors ${scrolled ? 'text-zinc-500 hover:text-black' : 'text-white/60 hover:text-white'}`}
        data-testid="nav-services-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        Services <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-72 bg-white border border-black/10 shadow-xl"
          style={{ animation: 'fadeInDown 0.15s ease-out' }}
          data-testid="nav-services-menu"
        >
          <div className="py-2">
            {serviceDropdownItems.map((item) => item.href ? (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className="block px-5 py-2.5 font-body text-xs uppercase tracking-wider text-zinc-600 hover:bg-yellow-400 hover:text-black transition-colors"
                data-testid={`nav-dropdown-${item.testId}`}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setIsOpen(false)}
                className="block px-5 py-2.5 font-body text-xs uppercase tracking-wider text-zinc-600 hover:bg-yellow-400 hover:text-black transition-colors"
                data-testid={`nav-dropdown-${item.to.split('/').pop()}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="border-t border-zinc-200 py-2">
            {serviceCategories.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setIsOpen(false)}
                className="block px-5 py-2.5 font-body text-xs uppercase tracking-wider text-yellow-600 font-semibold hover:bg-yellow-400 hover:text-black transition-colors"
                data-testid={`nav-dropdown-${item.to.split('/').pop()}`}
              >
                {item.label} &rarr;
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopNav({ scrolled }) {
  return (
    <div className="hidden lg:flex items-center gap-6">
      <ServicesDropdown scrolled={scrolled} />
      {navLinks.map((link) => (
        <a key={link.href} href={link.href} data-testid={`nav-link-${link.label.toLowerCase()}`}
          className={`font-body text-xs uppercase tracking-widest transition-colors ${scrolled ? 'text-zinc-500 hover:text-black' : 'text-white/60 hover:text-white'}`}>
          {link.label}
        </a>
      ))}
    </div>
  );
}

function MobileMenu({ onClose }) {
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-b border-black max-h-[80vh] overflow-y-auto">
      {/* Services accordion */}
      <button
        onClick={() => setServicesOpen(!servicesOpen)}
        className="w-full flex items-center justify-between px-6 py-3 font-body text-sm uppercase tracking-wider border-t border-zinc-200 hover:bg-yellow-400 hover:text-black transition-colors"
        data-testid="nav-mobile-services-toggle"
      >
        Services
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
      </button>
      {servicesOpen && (
        <div className="bg-zinc-50">
          {serviceDropdownItems.map((item) => item.href ? (
            <a
              key={item.href}
              href={item.href}
              onClick={onClose}
              className="block px-10 py-2.5 font-body text-xs uppercase tracking-wider text-zinc-600 border-t border-zinc-100 hover:bg-yellow-400 hover:text-black transition-colors"
              data-testid={`nav-mobile-svc-${item.testId}`}
            >
              {item.label}
            </a>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className="block px-10 py-2.5 font-body text-xs uppercase tracking-wider text-zinc-600 border-t border-zinc-100 hover:bg-yellow-400 hover:text-black transition-colors"
              data-testid={`nav-mobile-svc-${item.to.split('/').pop()}`}
            >
              {item.label}
            </Link>
          ))}
          {serviceCategories.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className="block px-10 py-2.5 font-body text-xs uppercase tracking-wider text-yellow-600 font-semibold border-t border-zinc-100 hover:bg-yellow-400 hover:text-black transition-colors"
            >
              {item.label} &rarr;
            </Link>
          ))}
        </div>
      )}

      {navLinks.map((link) => (
        <a key={link.href} href={link.href} onClick={onClose} data-testid={`nav-mobile-${link.label.toLowerCase()}`}
          className="block px-6 py-3 font-body text-sm uppercase tracking-wider border-t border-zinc-200 hover:bg-yellow-400 hover:text-black transition-colors">
          {link.label}
        </a>
      ))}
      <a href="/#contact" onClick={onClose} data-testid="nav-mobile-assistant"
        className="block px-6 py-3 font-body text-sm uppercase tracking-wider border-t border-zinc-200 bg-yellow-400 text-black font-bold text-center">
        Get Electrical Help
      </a>
      <Link to="/book" onClick={onClose} data-testid="nav-mobile-book"
        className="block px-6 py-3 font-body text-sm uppercase tracking-wider border-t border-zinc-200 bg-black text-white text-center">
        Book Now
      </Link>
      <a href="tel:+16159615930" data-testid="nav-mobile-phone" className="block px-6 py-3 bg-yellow-400 text-black font-bold text-center">
        Call (615) 961-5930
      </a>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-white/90 border-b border-black/10 shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
        <a href="/" data-testid="nav-logo" className="flex items-center gap-2 transition-colors">
          <img src={LOGO_URL} alt="Evolution Electric" className="h-20 object-contain" width={80} height={80} />
        </a>
        <DesktopNav scrolled={scrolled} />
        <div className="flex items-center gap-3">
          <a href="/#contact" data-testid="nav-assistant-btn"
            className="hidden sm:flex items-center gap-2 bg-yellow-400 text-black font-heading font-bold text-xs px-4 py-2 border border-black hover:-translate-y-0.5 transition-transform">
            <MessageSquare className="w-3 h-3" /> Get Electrical Help
          </a>
          <Link to="/book" data-testid="nav-book-btn"
            className="hidden sm:flex items-center gap-2 bg-black text-white font-heading font-bold text-xs px-4 py-2 border border-black hover:-translate-y-0.5 transition-transform">
            Book Now
          </Link>
          <a href="tel:+16159615930" data-testid="nav-phone-btn" onClick={() => trackPhoneCall('navbar')}
            className="hidden sm:flex items-center gap-2 bg-white/90 text-black font-heading font-bold text-xs px-4 py-2 border border-black hover:-translate-y-0.5 transition-transform">
            <Phone className="w-3 h-3" /> (615) 961-5930
          </a>
          <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle"
            className={`lg:hidden p-2 ${scrolled ? 'text-black' : 'text-white'}`}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </nav>
  );
}
